import React, { useState, useRef, useEffect } from 'react';
import { validateCode } from '../../utils/validation.js';
import { formatPhoneDisplay } from '../../utils/validation.js';
import Layout from '../Layout/Layout.jsx';
import styles from '../Common/Common.module.css';
import PageTitle from '../Common/PageTitle.jsx';
import codeStyles from './CodeStep.module.css';

const CodeStep = ({ phone, onVerify, onResend, isLoading }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    // Handle paste of multiple digits (iOS auto-fill)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newCode = [...code];
      
      // Fill remaining inputs starting from current index
      for (let i = 0; i < digits.length && index + i < 6; i++) {
        newCode[index + i] = digits[i];
      }
      
      setCode(newCode);
      setError('');
      
      // Focus the last filled input or submit if complete
      const lastIndex = Math.min(index + digits.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
      
      // Auto-submit if all 6 digits are filled
      if (newCode.join('').length === 6) {
        handleVerify(newCode.join(''));
      }
      return;
    }

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last digit
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6);
        if (digits.length === 6) {
          const newCode = digits.split('');
          setCode(newCode);
          inputRefs.current[5]?.focus();
          handleVerify(digits);
        }
      });
    }
  };

  const handleVerify = (codeString = code.join('')) => {
    const validation = validateCode(codeString);
    
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    onVerify(codeString);
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    
    setCode(['', '', '', '', '', '']);
    setError('');
    setResendCooldown(60); // 60 second cooldown
    onResend();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.card}>
        <PageTitle 
          title="ENTER VERIFICATION CODE" 
          subtitle={
            <>
              We sent a 6-digit code to<br />
              <strong>{formatPhoneDisplay(phone)}</strong>
            </>
          }
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              <path d="M13 8H7"/>
              <path d="M9 12H7"/>
            </svg>
          }
        />

        {error && (
          <div className={`${styles.alert} ${styles['alert-error']}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className={codeStyles['code-inputs']}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                className={`${codeStyles['code-input']} ${error ? codeStyles.error : ''}`}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                autoFocus={index === 0}
                autoComplete="off"
              />
            ))}
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={isLoading || code.join('').length !== 6}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                VERIFYING...
              </>
            ) : (
              <>
                VERIFY CODE
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 16 16 12 12 8"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
              </>
            )}
          </button>

          <button
            type="button"
            className={`${styles['button-secondary']} ${codeStyles['resend-button']}`}
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
          >
            {resendCooldown > 0 
              ? (
                <>
                  RESEND CODE ({resendCooldown} seconds)
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 16 16 12 12 8"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                </>
              )
              : (
                <>
                  RESEND CODE
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 16 16 12 12 8"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                </>
              )
            }
          </button>
        </form>

        <div className={codeStyles['info-box']}>
          <svg className={codeStyles['info-icon']} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12,2 15,9 22,10 17,15 18,22 12,18 6,22 7,15 2,10 9,9" />
          </svg>
          <div>
            <strong>The code expires in 10 minutes.</strong><br />
            Check your {phone.includes('@') ? 'email' : 'text messages'} for the code.
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default CodeStep;
